import React, { FC, createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Route, RouteComponentProps, Switch, useLocation, useParams } from "react-router";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import MessageEventModal from "go-component/components/MessageEvent/MessageEventModal";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { formatStringToDate } from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import RouteHistory from "go-core/utils/routeHistory";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { hasPermission, selectOrganization } from "go-security/services/organizations/selectors";
import { ProductsMenuContext } from "../../..";
import AppFormatResourceStatusLabel from "../../../../../../../../../components/FormatStatus";
import { ProductApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";
import {
	TransactionContext,
	TransactionResourceType,
} from "../../../../../components/Transaction/Context/TransactionContext";
import ProductContext from "../../../pages/Index/pages/Edit";
import OrganizationProductDocumentsPage from "../../../pages/Index/pages/Edit/pages/Documents";
import { mapProductRecipes } from "../../../utils";
import MergeProductModalForm from "../../MergeProductModalForm";
import ProductForm from "../../ProductForm/ProductForm";
import SubmenuProductNavigation from "../../SubmenuProductNavigation";
import { OrganizationProductAppLinksPage } from "../ProductAppLinks";
import { OrganizationProductContactIndexPage } from "../ProductContact";

export const ProductMobileActionsContext = createContext<MobileActionProps[]>([]);

interface MatchParams {
	productId: string;
}

export const onParseProductRecipes = (product: ProductApi) => {
	if (!product.recipes) return product;
	const newRecipes = [...product.recipes];
	newRecipes.sort((a, b) => {
		if (!a.valid_from) return -1;
		if (!b.valid_from) return 1;

		const aValidFromDate = formatStringToDate(a.valid_from);
		const bValidFromDate = formatStringToDate(b.valid_from);
		if (aValidFromDate > bValidFromDate) return 1;
		if (aValidFromDate < bValidFromDate) return -1;
		return 0;
	});
	product.recipes = newRecipes;
	return product;
};

const ProductEdit: FC<RouteComponentProps> = (props) => {
	const { match } = props;
	const { addFlash, addSuccessFlash } = useFlash();
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const { productId } = useParams<MatchParams>();
	const confirmation = useConfirmation();
	const params = {
		include:
			"category,connections,connections_full,full,custom_fields,custom_ids,tax,volumes_v2,open_linkeds,recipes,recipes.components.product.tax,recipes.components.product,components.products,components",
	};
	const [showLogs, setShowLogs] = useState<boolean>(false);
	const [showMergeProductModal, setShowMergeProductModal] = useState<boolean>(false);
	const { setTransactionResourceType, setListItemId } = useContext(TransactionContext);
	const { setMenuState } = useContext(ProductsMenuContext);
	const { product, setProduct, setCustomFieldsConfig, customFieldsConfig, productType, setProductType } =
		useContext(ProductContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { pathname } = useLocation();
	const displayNonListMobileActions = !pathname.includes("documents");
	const { isMobile } = useWindowSize();
	const hasAppLinksPermission = useSelector(hasPermission(["PRODUCT_LINKS", "SETTINGS"]));

	useEffect(() => {
		setTransactionResourceType(TransactionResourceType.PRODUCT);
		setListItemId(productId);
		onFetchResources();
		onFetchProduct();
	}, []);

	const onFetchProduct = async () => {
		try {
			const res = await api.organization().getProduct(Number(productId), params);
			const parsedRes = onParseProductRecipes(res);
			setProduct(parsedRes);
			setProductType(parsedRes.type);
			handleChangeTabTitle(
				`${parsedRes.name} | ${
					parsedRes.type === "PACKAGE" ? t("modules.package.header.title") : t("modules.product.header.title")
				}`
			);
			onUpdateProductsNavigation(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onFetchResources = async () => {
		try {
			const params = {
				resource_name: "PRODUCT",
				custom_field_resource_type: "PRODUCT",
				custom_field_permission_scope: "WRITE",
			};
			const res = await api.organization().getResourceTypes(params);
			setCustomFieldsConfig(res.custom_fields);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onUpdateProductsNavigation = (product: ProductApi) => {
		switch (product.type) {
			case "PACKAGE":
				setMenuState("packages");
				break;
			case "COMPOSITE":
				setMenuState("composites");
				break;
			case "BASIC":
			default:
				setMenuState("products");
				break;
		}
	};

	if (!product || !customFieldsConfig) return <LoadingContainer />;

	const onRemoveProduct = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			const res = await api.organization().removeProduct(Number(productId), params);
			addSuccessFlash(t("lib:common.flash.removed"));
			setProduct(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onRestoreProduct = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.activate", { ns: "lib" }),
			});
			const res = await api.organization().restoreProduct(Number(productId), params);
			addSuccessFlash(t("lib:common.flash.restored"));
			setProduct(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onCloseLinkedProduct = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const res = await api.organization().closeLinkedProduct(Number(productId), params);
			addSuccessFlash(t("lib:common.flash.completed"));
			setProduct(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onChangeProductToPackage = () => {
		setProductType("PACKAGE");
	};

	const onChangeToBasicProduct = () => {
		setProductType("BASIC");
	};

	const onChangeToCompositeProduct = () => {
		setProductType("COMPOSITE");
	};

	const handleCopyProduct = () => {
		const productToCopy = {
			...product,
			sku: undefined,
			custom_ids: [],
			connections: [],
			custom_fields: mapCustomFieldsToForm(customFieldsConfig || [], product?.custom_fields || []),
			recipes: mapProductRecipes(product),
			id: undefined,
		};

		history.push({
			pathname: `/${organization.id}/products/new`,
			state: {
				copiedItem: {
					...productToCopy,
					name: `${t("common.word.copy")} ${productToCopy.name}`,
				},
				type: product.type,
			},
		});
	};

	const handleRecipeCorrection = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await api.organization().correctProductRecipe(product.id);
			onFetchProduct();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const goBack = () => {
		if (product.type === "PACKAGE") {
			RouteHistory.goBack(history, `/${organization.id}/products/packages`);
		} else if (product.type === "COMPOSITE") {
			RouteHistory.goBack(history, `/${organization.id}/products/composite`);
		} else {
			RouteHistory.goBack(history, `/${organization.id}/products`);
		}
	};

	const renderTitle = () => {
		let titleFirstElement = "";

		if (productType === "PACKAGE") {
			titleFirstElement = t("modules.package.header.edit.title");
		} else if (productType === "COMPOSITE") {
			titleFirstElement = t("modules.product_composite.header.edit.title");
		} else {
			titleFirstElement = t("modules.product.header.edit.title");
		}

		const title = `${titleFirstElement} "${product.name}"`;

		return (
			<>
				{title}
				<AppFormatResourceStatusLabel status={product.status} />
			</>
		);
	};

	const buttons: ButtonProps[] = [
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(true),
			variant: "light",
		},
		{
			title: t("modules.product.action.merge.title"),
			action: () => setShowMergeProductModal(true),
			dropdown: true,
		},
		{
			title: t("lib:common.action.copy"),
			action: handleCopyProduct,
			dropdown: true,
		},
		{
			title: t("modules.product.action.change_to_composite_product.title"),
			action: onChangeToCompositeProduct,
			dropdown: true,
			hide: productType === "COMPOSITE",
		},
	];
	if (product.status !== "DELETED")
		buttons.push({
			title: t("lib:common.action.remove"),
			action: onRemoveProduct,
			dropdown: true,
		});
	if (product.status === "DELETED")
		buttons.push({
			title: t("lib:common.action.restore"),
			action: onRestoreProduct,
			dropdown: true,
		});

	if (product.open_linkeds)
		buttons.push({
			title: t("modules.product.action.close_linked_products.title"),
			action: onCloseLinkedProduct,
			dropdown: true,
		});
	if (productType !== "PACKAGE")
		buttons.push({
			title: t("modules.product.action.change_to_package.title"),
			action: onChangeProductToPackage,
			dropdown: true,
		});
	if (productType === "PACKAGE" || productType === "COMPOSITE")
		buttons.push({
			title: t("modules.product.action.change_to_basic_product.title"),
			action: onChangeToBasicProduct,
			dropdown: true,
		});
	if (productType === "PACKAGE")
		buttons.push({
			title: t("common.action.recipe_correction"),
			action: handleRecipeCorrection,
			dropdown: true,
		});

	const mobileActions: MobileActionProps[] = [
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(true),
		},
		{
			title: t("modules.product.action.merge.title"),
			action: () => setShowMergeProductModal(true),
		},
		{
			title: t("lib:common.action.copy"),
			action: handleCopyProduct,
		},
		{
			title: t("lib:common.action.remove"),
			action: onRemoveProduct,
			hide: product.status === "DELETED",
		},
		{
			title: t("lib:common.action.restore"),
			action: onRestoreProduct,
			hide: product.status !== "DELETED",
		},
		{
			title: t("modules.product.action.close_linked_products.title"),
			action: onCloseLinkedProduct,
			hide: !product.open_linkeds,
		},
		{
			title: t("modules.product.action.change_to_package.title"),
			action: onChangeProductToPackage,
			hide: productType === "PACKAGE",
		},
		{
			title: t("modules.product.action.change_to_basic_product.title"),
			action: onChangeToBasicProduct,
			hide: productType !== "PACKAGE" && productType !== "COMPOSITE",
		},
		{
			title: t("common.action.recipe_correction"),
			action: handleRecipeCorrection,
			hide: productType !== "PACKAGE",
		},
		{
			title: t("modules.product.action.change_to_composite_product.title"),
			action: onChangeToCompositeProduct,
			hide: productType !== "PACKAGE" && productType !== "BASIC",
		},
	];

	return (
		<>
			{displayNonListMobileActions && <MobileActions actions={mobileActions} />}
			<Header title={renderTitle()} back={goBack} buttons={isMobile ? [] : buttons} />
			<SubmenuProductNavigation productId={productId} />
			<ProductMobileActionsContext.Provider value={mobileActions}>
				<Switch>
					<Route path={`${match.url}/documents`} component={OrganizationProductDocumentsPage} />
					{hasAppLinksPermission && (
						<Route path={`${match.url}/app_links`} component={OrganizationProductAppLinksPage} />
					)}
					{/* <Route path={`${match.url}/transactions`} exact component={OrganizationTransactionIndexPage} /> */}
					<Route path={`${match.url}/contacts`} component={OrganizationProductContactIndexPage} />
					<Route path="" exact component={ProductForm} />
				</Switch>
			</ProductMobileActionsContext.Provider>
			{showLogs && (
				<MessageEventModal
					path={`/${organization.id}/logs/message_events`}
					resourceId={product.id}
					resourceType="PRODUCT"
					onHide={() => setShowLogs(!showLogs)}
					organizationId={organization.id}
				/>
			)}
			<MergeProductModalForm
				isShown={showMergeProductModal}
				onHide={() => setShowMergeProductModal(false)}
				onSuccessAction={setProduct}
				productId={product.id}
			/>
		</>
	);
};

export default ProductEdit;
